import moment from "moment";
import React from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import downloadImg from "../../src/assets/icons/download.png";
import { lookupPhone as defaultLookupPhone } from "./api";

export const fBuildVersion = "v2.4.0";

export const numberMask = createNumberMask({
  prefix: "$",
  allowDecimal: true,
});

export const amountMask = createNumberMask({
  prefix: "$",
  allowDecimal: true,
  decimalSymbol: ".",
});

export const percentMask = createNumberMask({
  suffix: "%",
  prefix: "",
  allowDecimal: true,
  includeThousandsSeparator: false, // Adjust this based on your needs
  allowNegative: false, // Adjust this based on your needs
  decimalLimit: 2, // Adjust this based on your needs
  decimalSymbol: ".",
  integerLimit: 3, // Adjust this based on your needs
});

export const quantityMask = createNumberMask({
  prefix: "",
  integerLimit: 4,
  allowDecimal: true,
  includeThousandsSeparator: false,
});

export const quantityMaskMax = createNumberMask({
  prefix: "",
  integerLimit: 4,
  decimalLimit: null,
  allowDecimal: true,
  includeThousandsSeparator: false,
});

export function getTimeToDisplay(time) {
  var a = moment();
  var b = moment(time);

  let days = a.diff(b, "days");
  let hours = a.diff(b, "hours");
  let min = a.diff(b, "minutes");
  let displayDate;

  if (hours == 0) {
    if (min >= 0) {
      if (min == 1) {
        displayDate = min + " MINUTE AGO";
      } else {
        displayDate = min + " MINUTES AGO";
      }
    } else {
      displayDate = "0" + " MINUTE AGO";
    }
  } else if (days == 0) {
    if (hours == 1) {
      displayDate = hours + " HOUR AGO";
    } else {
      displayDate = hours + " HOURS AGO";
    }
  } else if (days == 1) {
    displayDate = days + " DAY AGO";
  } else {
    displayDate = days + " DAYS AGO";
  }
  return displayDate;
}

export function validateEmail(email) {
  var re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

/** Strip non-digits for SMS / phone payloads. */
export function normalizePhone(phone) {
  return String(phone || "").replace(/\D/g, "");
}

/** Accepts 10–15 digits (US 10-digit or intl). Strips formatting first. */
export function validatePhone(phone) {
  const digits = normalizePhone(phone);
  return digits.length >= 10 && digits.length <= 15;
}

export async function verifySmsReceivablePhone(user, rawPhone, options = {}) {
  const { requiredMessage = "Phone number is required", lookupFn } = options;
  const phoneVal = String(rawPhone || "").trim();

  if (!phoneVal) {
    return { ok: false, message: requiredMessage, title: "Alert" };
  }
  if (!validatePhone(phoneVal)) {
    return {
      ok: false,
      message: "Please enter a valid phone number (10–15 digits)",
      title: "Alert",
    };
  }

  const normalizedPhone = normalizePhone(phoneVal) || phoneVal;
  try {
    const response = await (lookupFn || defaultLookupPhone)(
      user,
      normalizedPhone
    );

    if (!response || response.status_code !== 200) {
      return {
        ok: false,
        normalizedPhone,
        message: response?.message || "Failed to verify phone number for SMS",
        title: "Error!",
      };
    }

    const data = response?.data;
    const canReceive = data?.valid !== false && !!data?.can_receive_sms;
    if (!canReceive) {
      return {
        ok: false,
        normalizedPhone,
        message:
          response?.message || "This phone number cannot receive text/SMS",
        title: "Error!",
      };
    }

    return { ok: true, normalizedPhone };
  } catch (error) {
    return {
      ok: false,
      normalizedPhone,
      message: "Failed to verify phone number for SMS",
      title: "Error!",
    };
  }
}

export function validateCompanyEmail(email) {
  var re =
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
  return re.test(email);
}

// Validates that the input string is a valid date formatted as "mm/dd/yyyy"
// Source: https://stackoverflow.com/questions/6177975/how-to-validate-date-with-format-mm-dd-yyyy-in-javascript
export function isValidDate(dateString, upperYearLimit) {
  // First check for the pattern
  if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) return false;
  // Parse the date parts to integers
  var parts = dateString.split("/");
  var month = parseInt(parts[0], 10);
  var day = parseInt(parts[1], 10);
  var year = parseInt(parts[2], 10);

  // Check the ranges of month and year
  if (upperYearLimit) {
    if (year < 1910 || year > upperYearLimit || month === 0 || month > 12)
      return false;
  } else {
    if (year < 1910 || month === 0 || month > 12) return false;
  }

  var monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Adjust for leap years
  if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0))
    monthLength[1] = 29;

  // Check the range of the day
  return day > 0 && day <= monthLength[month - 1];
}

// takes the form field value and returns true on valid number
export function isValidCreditCard(value) {
  // accept only digits, dashes or spaces
  if (/[^0-9-\s]+/.test(value)) return false;

  // The Luhn Algorithm. It's so pretty.
  var nCheck = 0,
    bEven = false;
  value = value.replace(/\D/g, "");

  for (var n = value.length - 1; n >= 0; n--) {
    var cDigit = value.charAt(n),
      eDigit = parseInt(cDigit, 10);

    if (bEven) {
      eDigit *= 2;
      if (eDigit > 9) {
        eDigit -= 9;
      }
    }

    nCheck += eDigit;
    bEven = !bEven;
  }

  return nCheck % 10 === 0;
}

export function getRoundedAmount(amount, decimalPlaces = 2) {
  const round = (num, digits = 2) =>
    Math.round(num * Math.pow(10, digits)) / Math.pow(10, digits);
  return round(amount, decimalPlaces);
}

export function getDollarAmountNew(string) {
  let amount = "0.00";
  if (string) {
    amount = getRoundedAmount(parseFloat(string), 2)
      .toFixed(2)
      .replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
  }
  return amount === "NaN" ? "$0.00" : `$${amount}`;
}

export function getDollarAmount(string, decimalPlaces = 2) {
  let amount = "0.00";
  if (string) {
    amount = getRoundedAmount(parseFloat(string), decimalPlaces)
      .toFixed(decimalPlaces)
      .replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
  }
  return amount === "NaN" ? "$ 0.00" : `$ ${amount}`;
}

export function getFormattedAmount(value, decimalPlaces = 2) {
  let amount = "0.00";

  if (value) {
    amount = parseFloat(value)
      .toFixed(decimalPlaces)
      .replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
  }

  return amount === "NaN" ? "0.00" : amount;
}

// A failed payment call answers with a general `message` and, when the
// processor refused the charge, its reason in `failure_message`. The error
// popup shows both as one line (toasts don't keep line breaks).
export function getPaymentErrorMessage(data, fallback = "") {
  const message = String(data?.message || fallback || "").trim();
  const failureMessage =
    typeof data?.failure_message === "string"
      ? data.failure_message.trim()
      : "";
  if (!failureMessage || message.includes(failureMessage)) return message;
  if (!message) return failureMessage;
  const separator = /[.!?]$/.test(message) ? " " : ". ";
  return `${message}${separator}${failureMessage}`;
}

// Splitting camelCase turns the brand into "Make Good"; it is always one word.
export function keepMakeGoodBrand(label) {
  return String(label).replace(/\bmake\s?good\b/gi, "MakeGood");
}

export function formatPaymentChannelLabel(value) {
  if (value == null || String(value).trim() === "") return "--";
  const spaced = String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
  return keepMakeGoodBrand(
    spaced
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
  );
}

export function getDollarAmountWithLargeDecimals(string) {
  let num = parseFloat(string);
  if (isNaN(num)) return "$ 0.00";

  let formatted =
    num % 1 === 0
      ? num.toFixed(2)
      : num.toFixed(8).replace(/(?:\.0+|(?<=\.\d*?[1-9])0+)$/, "");

  // Add comma separators
  const parts = formatted.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return `$ ${parts.join(".")}`;
}

export function getCalculatedPlanAmount(
  total,
  discount,
  credit_memo_flag,
  status,
  type
) {
  // if (status === "offer" || status === "offer_sent" || !credit_memo_flag) {
  if (false) {
    if (type && type === "Plan_amt") {
      let amt = parseFloat(total.replace(",", "")).toFixed(2);
      return amt;
    } else if (type && type === "org_balance") {
      if (parseFloat(total.replace(",", "")) <= 0) {
        return "0.00";
      } else {
        let amt = parseFloat(
          parseFloat(total.replace(",", "")) +
            parseFloat(discount.replace(",", ""))
        ).toFixed(2);
        return amt;
      }
    } else {
      return "0.00";
    }
  } else {
    if (type && type === "Plan_amt") {
      if (parseFloat(total?.replace(",", "")) <= 0) {
        return "0.00";
      } else {
        let amt = parseFloat(
          parseFloat(total?.replace(",", "")) -
            parseFloat(discount?.replace(",", ""))
        ).toFixed(2);
        return amt;
      }
    } else if (type && type === "org_balance") {
      let amt = parseFloat(total?.replace(",", "")).toFixed(2);
      return amt;
    } else {
      return "0.00";
    }
  }
}

// export function getCalculatedPlanAmount(
//   total,
//   discount,
//   credit_memo_flag,
//   status,
//   type
// ) {
//   if (status === "offer" || status === "offer_sent" || !credit_memo_flag) {
//     if (type && type === "Plan_amt") {
//       let amt = parseFloat(total.replace(",", "")).toFixed(2);
//       return amt;
//     } else if (type && type === "org_balance") {
//       if (parseFloat(total.replace(",", "")) <= 0) {
//         return "0.00";
//       } else {
//         let amt = parseFloat(
//           parseFloat(total.replace(",", "")) +
//             parseFloat(discount.replace(",", ""))
//         ).toFixed(2);
//         return amt;
//       }
//     } else {
//       return "0.00";
//     }
//   } else {
//     if (type && type === "Plan_amt") {
//       if (parseFloat(total.replace(",", "")) <= 0) {
//         return "0.00";
//       } else {
//         let amt = parseFloat(
//           parseFloat(total.replace(",", "")) -
//             parseFloat(discount.replace(",", ""))
//         ).toFixed(2);
//         return amt;
//       }
//     } else if (type && type === "org_balance") {
//       let amt = parseFloat(parseFloat(total.replace(",", ""))).toFixed(2);
//       return amt;
//     } else {
//       return "0.00";
//     }
//   }
// }

export function formatRelativeTime(raw, options = {}) {
  const {
    justNowThresholdSeconds = null,
    absoluteDateAfterDays = null,
    emptyValue = null,
    coerceEpochSeconds = false,
  } = options;

  if (raw == null && emptyValue != null) {
    return emptyValue;
  }

  const normalized =
    coerceEpochSeconds && typeof raw === "number"
      ? raw < 1e12
        ? raw * 1000
        : raw
      : raw;

  const dateObj = new Date(normalized);
  const now = new Date();

  if (Number.isNaN(dateObj.getTime()) && emptyValue != null) {
    return emptyValue;
  }

  const diffInMilliseconds = now.getTime() - dateObj.getTime();
  if (diffInMilliseconds < 0 && emptyValue != null) {
    return emptyValue;
  }

  const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
  const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
  const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (
    justNowThresholdSeconds != null &&
    diffInSeconds < justNowThresholdSeconds
  ) {
    return "Just Now";
  }

  if (absoluteDateAfterDays != null && diffInDays >= absoluteDateAfterDays) {
    return dateObj.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  if (diffInSeconds < 60) return `${diffInSeconds} sec ago`;
  if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
  if (diffInHours < 24)
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  if (diffInDays === 1) return "1 day ago";
  if (diffInDays < 7) return `${diffInDays} days ago`;

  if (diffInWeeks === 1) return "1 week ago";
  if (diffInWeeks < 4) return `${diffInWeeks} weeks ago`;
  if (diffInMonths === 1) return "1 month ago";
  if (diffInMonths < 12) return `${diffInMonths} months ago`;
  if (diffInYears === 1) return "1 year ago";
  return `${diffInYears} years ago`;
}

export const TERMINAL_CUSTOMER_SYNC_STATUSES = new Set([
  "completed",
  "failed",
  "cancelled",
]);

export const isActiveCustomerSyncStatus = (status) =>
  !!status && !TERMINAL_CUSTOMER_SYNC_STATUSES.has(status);

export const getCustomerSyncRunTimestamp = (syncRun) => {
  if (!(syncRun && typeof syncRun === "object")) return null;

  const fields = [
    "completed_at",
    "finished_at",
    "last_synced_at",
    "updated_at",
    "created_at",
  ];
  const raw = fields.map((field) => syncRun[field]).find((val) => val != null);
  return raw ?? null;
};

export const getFormattedDate = (isoString) => {
  const dateObj = new Date(isoString);

  const date = dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const time = dateObj.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    // second: "2-digit",
    hour12: true,
  });

  return `${date}`;
};

export function getInvoiceDownload(item, downloadInvoice) {
  if (item.document_id !== null) {
    return (
      <div className='Action-buttons'>
        <button
          title='Download'
          style={{
            background: "transparent",
            cursor: "pointer",
            marginRight: 15,
          }}
          onClick={() =>
            downloadInvoice(
              item.document_id,
              `${item.source === "Credit Memo" ? "CreditMemo" : "Invoice"}_${
                item.invoice_number
                  ? item.invoice_number
                  : item.transaction_number
              }.pdf`
            )
          }
        >
          <img src={downloadImg} width='16' height='16' />
        </button>
      </div>
    );
  }
}
export function displayPlanStatus(status) {
  if (status === "offer") {
    return "Offer";
  } else if (status === "active") {
    return "Active";
  } else if (status === "inactive") {
    return "Inactive";
  } else if (status === "offer_sent") {
    return "Offer Sent";
  } else if (status === "closed") {
    return "Closed";
  } else if (status === "paid") {
    return "Paid";
  } else if (status === "due") {
    return "Payment Due";
  } else if (status === "not_due") {
    return "Not Due";
  } else if (status === "past_due") {
    return "Past Due";
  } else if (status === "partially_paid") {
    return "Partially Paid";
  } else if (status === "on_time") {
    return "On Time";
  } else {
    return "Unknown";
  }
}
export function getInvoiceNumber(item, handleClick) {
  return (
    <a
      style={{ textDecoration: "none", cursor: "pointer" }}
      onClick={() => {
        handleClick(item);
      }}
    >
      {item.invoice_number}
    </a>
  );
}

export function getWithoutDollarAmount(string, type) {
  let amount = type === "onlyNumber" ? "0" : "0.00";

  if (string) {
    if (type === "onlyNumber") {
      amount = parseInt(string)
        .toFixed(0)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    } else {
      amount = roundToTwoDecimals(parseFloat(string))
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
  }

  return amount;
}

export function roundToTwoDecimals(amount) {
  return Math.round(amount * 100) / 100;
}

export function getStrippedDollarAmount(string) {
  let strippedNumber = string ? string.replace(/[$,]/g, "") : "";

  return roundToTwoDecimals(
    strippedNumber === "" ? 0 : parseFloat(strippedNumber)
  );
}

export function getStrippedNumberFromString(string) {
  let strippedNumber = string ? string.replace(/[\$\,\(\)\-\ ]/g, "") : "";

  return roundToTwoDecimals(
    strippedNumber === "" ? 0 : parseFloat(strippedNumber)
  );
}

export function convertCreditorsToArrayForSelectComponent(creditorsArray) {
  let newCreditors = [...creditorsArray];

  for (var i = 0; i < newCreditors.length; i++) {
    newCreditors[i].value = newCreditors[i]["id"];
    delete newCreditors[i].id;
    newCreditors[i].label = newCreditors[i]["name"];
    delete newCreditors[i].name;
  }

  return newCreditors;
}

export function getNumberWithCommas(number) {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const numberMaskForPercent = createNumberMask({
  prefix: "",
  suffix: "%",
  allowDecimal: true,
  includeThousandsSeparator: false,
  integerLimit: 3,
});

export function getStrippedDollarOrPercentAmount(string) {
  let strippedNumber;
  if (string.includes("$")) {
    strippedNumber = string.replace(/\$|,/g, "");
  } else {
    strippedNumber = string.replace(/\%|,/g, "");
  }
  return strippedNumber === "" ? 0 : parseFloat(strippedNumber);
}

// Decode base64 into a Blob so previews can use a blob: URL; the hosting CSP
// blocks <object>/<embed> and data: frames. Returns null for invalid base64.
export function base64ToBlob(base64, mimeType) {
  try {
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  } catch (e) {
    return null;
  }
}

export const showToast = (msg) => {
  toast.dismiss();
  const position = toast.POSITION?.TOP_RIGHT || "top-right"; // Safe fallback
  let message = msg
    ? msg
    : "Unable to process a request. Please try again later!";
  toast.info(message, {
    position: position,
    // className: 'primary-color'
  });
};

export const showConfirmAlert = (msg, sucessCallBack, errorCallBack) => {
  return confirmAlert({
    // title: 'Confirm',
    message: msg,
    buttons: [
      {
        label: "Yes",
        onClick: () => sucessCallBack(),
      },
      {
        label: "Cancel",
        onClick: () => (errorCallBack ? errorCallBack() : ""),
      },
    ],
  });
};

export function getZohoPagesense() {
  return new Promise(function (resolve, reject) {
    var script = document.createElement("script");
    // for staging
    //script.src = "https://cdn.pagesense.io/js/readytechs/7748726604f14d32a5d38ce9c2813e63.js";
    // for production
    script.src =
      "https://cdn.pagesense.io/js/readytechs/fe10fb27979e4aa38d8e9971cef1b295.js";
    script.addEventListener("load", function () {
      resolve();
    });
    script.addEventListener("error", function (e) {
      reject(e);
    });
    document.body.appendChild(script);
  });
}

//export const googleAnalyticId = "UA-148090034-1" //staging
//export const googleAnalyticId = "UA-148090034-2" //production
// export const googleAnalyticId = "UA-148090034-3"; //new production
export const googleAnalyticId = "G-DP5177J4VJ"; //new production 11/08/2023

export const networkMessage =
  "Please check your internet connection and try again.";
export const errorMessage = "Something went wrong. Please try again.";

export function isNumber(n) {
  return /^-?[\d.]+(?:e-?\d+)?$/.test(n);
}

export function isAlphaNumericWithHyphenUnderscore(n) {
  return /^-?[\w-_]+?$/.test(n);
}

export function isSameStartDateAndDueDate(due_date) {
  let time1 = moment().format("YYYY-MM-DD");
  let time2 = moment(due_date).format("YYYY-MM-DD");
  if (time2 == time1) {
    return true;
  } else {
    return false;
  }
}

export function getFormattedDateYYYYMMDD(date) {
  if (date) return moment(new Date(date)).format("YYYY-MM-DD");
  return date;
}

export function getFormattedDateMMDDYYYY(date) {
  if (date) return moment(date).format("MM/DD/YYYY");
  return date;
}

export function formatDate(date) {
  const newDate = moment(date).format("MM/DD/YYYY HH:mm A");
  return newDate.split(" ");
}

export function checkValueInKey(key) {
  if (key === null || key === "") {
    return false;
  } else {
    return true;
  }
}

export const STATUS_COLORS = {
  PENDING: "#FFA500",
  FAILED: "#FF0000",
  SUCCESS: "#00C851",
};

export const getStatusIndicatorColor = (status) => {
  const normalizedStatus = (status || "").toLowerCase().trim();

  if (normalizedStatus === "pending" || normalizedStatus === "in progress") {
    return STATUS_COLORS.PENDING;
  }
  if (
    normalizedStatus === "failed" ||
    normalizedStatus === "aborted" ||
    normalizedStatus === "cancelled"
  ) {
    return STATUS_COLORS.FAILED;
  }
  if (
    normalizedStatus === "completed" ||
    normalizedStatus === "success" ||
    normalizedStatus === "complete"
  ) {
    return STATUS_COLORS.SUCCESS;
  }
  return null;
};

export const formatDeliveryMethod = (deliveryMethod) => {
  if (deliveryMethod === "Email" || deliveryMethod === "Email Only") {
    return "Email Only";
  }
  if (deliveryMethod === "Letter" || deliveryMethod === "Letter Only") {
    return "Letter Only";
  }
  if (deliveryMethod === "Text" || deliveryMethod === "Text Only") {
    return "Text Only";
  }
  if (
    deliveryMethod === "Email_and_Text" ||
    deliveryMethod === "Email & Text"
  ) {
    return "Email & Text";
  }
  if (
    deliveryMethod === "Email_and_Letter" ||
    deliveryMethod === "Email & Letter" ||
    (deliveryMethod &&
      deliveryMethod.includes("and") &&
      deliveryMethod.includes("Letter"))
  ) {
    return "Email & Letter";
  }
  return "Email & Letter";
};

export const getDepositVal = (obj, ...keys) => {
  if (!obj || typeof obj !== "object") return undefined;
  for (const k of keys) {
    if (k == null) continue;
    const v = obj[k];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
};

export const resolveDepositSurcharge = (data, d, payment_config) => {
  const orgSurchargePct =
    getDepositVal(data, "surcharge_amount", "surchargeAmount") ??
    getDepositVal(data?.data, "surcharge_amount", "surchargeAmount") ??
    getDepositVal(
      data?.payment_config,
      "surcharge_amount",
      "surchargeAmount"
    ) ??
    getDepositVal(
      data?.data?.payment_config,
      "surcharge_amount",
      "surchargeAmount"
    );
  const orgSurchargeOn =
    getDepositVal(
      data,
      "surcharge_enabled",
      "surchargeEnabled",
      "elavon_surcharge_enabled"
    ) ??
    getDepositVal(
      data?.data,
      "surcharge_enabled",
      "surchargeEnabled",
      "elavon_surcharge_enabled"
    );

  return {
    surcharge_amount:
      getDepositVal(d, "surcharge_amount", "surchargeAmount") ??
      getDepositVal(payment_config, "surcharge_amount", "surchargeAmount") ??
      orgSurchargePct ??
      data?.surcharge_amount,
    surcharge_enabled:
      getDepositVal(
        d,
        "surcharge_enabled",
        "surchargeEnabled",
        "elavon_surcharge_enabled"
      ) ??
      getDepositVal(
        payment_config,
        "surcharge_enabled",
        "surchargeEnabled",
        "elavon_surcharge_enabled"
      ) ??
      orgSurchargeOn ??
      data?.surcharge_enabled,
  };
};
